import { HttpClient } from "./HttpClient";
import { AxiosRequestConfig, AxiosResponse } from "axios";
import { InterceptorManager } from "./InterceptorManager";
import { HttpBase } from "./HttpBase";

export type HttpClientManagerDefaults = Omit<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  AxiosRequestConfig<any>,
  "headers"
>;

export const initHttpClients = <
  CUSTOM_CONFIG,
  CLIENT extends HttpClient<CUSTOM_CONFIG>,
  CLIENT_SET extends Record<string, CLIENT>
>(
  clients: CLIENT_SET
) => {
  return () => clients;
};

export class HttpClientManager extends HttpBase {
  public httpKey = "http";

  public defaultConfig: HttpClientManagerDefaults = {};

  public readonly requestInterceptors: {
    readonly clientFront: InterceptorManager<AxiosRequestConfig>;
    readonly clientPost: InterceptorManager<AxiosRequestConfig>;
  };

  public readonly responseInterceptors: {
    readonly clientFront: InterceptorManager<AxiosResponse>;
    readonly clientPost: InterceptorManager<AxiosResponse>;
  };

  constructor(defaultConfig: HttpClientManagerDefaults = {}) {
    super();
    this.defaultConfig = defaultConfig;
    this.requestInterceptors = {
      clientFront: new InterceptorManager(),
      clientPost: new InterceptorManager(),
    };
    this.responseInterceptors = {
      clientFront: new InterceptorManager(),
      clientPost: new InterceptorManager(),
    };
  }

  register<CUSTOM_CONFIG>(httpClient: HttpClient<CUSTOM_CONFIG>) {
    const requestFrontInter = this.requestInterceptors.clientFront.getAll();
    const requestPostInter = this.requestInterceptors.clientPost.getAll();
    const responseFrontInter = this.responseInterceptors.clientFront.getAll();
    const responsePostInter = this.responseInterceptors.clientPost.getAll();

    for (let i = requestFrontInter.length - 1; i >= 0; i--) {
      const { fulfilled, rejected } = requestFrontInter[i];
      httpClient.interceptors.request.unshift(fulfilled, rejected);
    }

    for (let i = responseFrontInter.length - 1; i >= 0; i--) {
      const { fulfilled, rejected } = responseFrontInter[i];
      httpClient.interceptors.response.unshift(fulfilled, rejected);
    }

    for (let i = 0, max = requestPostInter.length - 1; i <= max; i++) {
      const { fulfilled, rejected } = requestPostInter[i];
      httpClient.interceptors.request.use(fulfilled, rejected);
    }

    for (let i = 0, max = responsePostInter.length - 1; i <= max; i++) {
      const { fulfilled, rejected } = responsePostInter[i];
      httpClient.interceptors.response.use(fulfilled, rejected);
    }

    httpClient.bindInterceptor();
    httpClient.updateDefaultConfig(this.defaultConfig, "onlyAdd");

    return httpClient;
  }
}
